import { Progress } from '@/components/ui/progress'
import { useGuild } from 'app/frontend/base/config/context/guild'
import TeamContext, { useTeam } from 'app/frontend/base/config/context/team'
import {
  useApplicationDetails,
  usePageInfo,
  useTeamDetails,
  useUserApplications,
  useUserCharacterDetails,
} from 'app/frontend/base/config/queries'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Select from 'app/frontend/base/components/Select'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Button, buttonVariants } from '@/components/ui/button'
import { useEffect, useMemo, useState } from 'react'
import useDraftStorage from 'app/frontend/base/hooks/useDraftStorage'
import ApplicationCharacterCard from 'app/frontend/base/components/ApplicationCharacterCard'
import LoginButtons from 'app/frontend/base/components/LoginButtons'
import RoleSelector from './RoleSelector'
import CompactLinkedAccount from './CompactLinkedAccount'
import {
  createApplication,
  updateApplicationWithFormData,
} from 'app/frontend/base/api/application'
import ApplicationQuestion from './ApplicationQuestion'
import { displayErrorToast, imageUrl } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { CheckCircle2, Info, XCircle } from 'lucide-react'
import UserApplicationDetails from './UserApplicationDetails'
import { updateCharacter } from 'app/frontend/base/api/character'
import dayjs from 'dayjs'
import { applicationStatusLabels } from 'app/frontend/types/custom/ApplicationStatus'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import TroubleshootingModal from 'app/frontend/base/pages/UserDashboard/components/TroubleshootingModal'
import { defaultLegacyFormColors, urlToMainSite } from '../utils'
import TeamRecruitmentDetails from 'app/frontend/types/custom/TeamRecruitmentDetails'
import JoinDiscordButton from './JoinDiscordButton'
import { toast } from 'sonner'
import Card from 'app/frontend/base/components/Card'
import { useRefreshData } from 'app/frontend/base/hooks/useRefreshData'

export const TeamApplicationForm = ({
  editing = false,
}: ApplicationFormProps) => {
  return <ApplicationForm editing={editing} />
}

type ApplicationFormProps = {
  inDashboard?: boolean
  editing: boolean
}

export const GuildApplicationForm = ({
  inDashboard = true,
  editing = false,
}: ApplicationFormProps) => {
  const { guild } = useGuild()
  const navigate = useNavigate()

  const {
    data: team,
    isPending,
    refetch: refetchTeamDetails,
  } = useTeamDetails(inDashboard ? guild?.defaultTeam?.id : undefined)

  if (guild?.defaultTeam && inDashboard && isPending) {
    return (
      <Progress
        className='-mx-8 -mt-6 w-[calc(100%+4rem)] rounded-none bg-transparent h-1'
        indeterminate
      />
    )
  }

  if ((!guild?.defaultTeam || !inDashboard) && !team) {
    const content = (
      <div className='flex flex-col gap-2 grow'>
        <Label>Team</Label>
        <Select
          options={(guild?.teamReferences || []).map((t) => ({
            label: t.name,
            value: t.path,
          }))}
          placeholder={'Select a team to apply to'}
          setValue={(value) => {
            if (value) navigate(`${value}${inDashboard ? '/apply' : ''}`)
          }}
        />
      </div>
    )

    if (inDashboard) {
      return (
        <>
          <h2>Apply to {guild?.name}</h2>
          <Card className='p-6 max-w-3xl'>{content}</Card>
        </>
      )
    } else {
      return content
    }
  }

  if (!guild || !team) {
    return null
  }

  return (
    <TeamContext.Provider value={{ guild, team, refetchTeamDetails }}>
      <ApplicationForm inDashboard={inDashboard} editing={editing} />
    </TeamContext.Provider>
  )
}

const createFormSchema = (
  showCountry: boolean,
  showAge: boolean,
  requiredQuestionIds: number[],
) =>
  z
    .object({
      role: z.enum(['tank', 'heal', 'damage']),
      character_id: z.number(),
      country: z.string().optional(),
      age: z.number().optional().nullable(),
      notify_on_discord: z.boolean(),
      notify_by_email: z.boolean(),
      answers: z.record(z.string(), z.string().optional().nullable()),
    })
    .superRefine((data, ctx) => {
      if (showCountry && (!data.country || data.country.trim() === '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Country is required',
          path: ['country'],
        })
      }

      if (showAge && !data.age) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Age is required',
          path: ['age'],
        })
      }

      for (const questionId of requiredQuestionIds) {
        const answer = data.answers[questionId.toString()]
        if (!answer || answer.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'This field is required',
            path: ['answers', questionId.toString()],
          })
        }
      }
    })

type FormValues = z.infer<ReturnType<typeof createFormSchema>>

const ApplicationForm = ({
  inDashboard = true,
  editing,
}: ApplicationFormProps) => {
  const { t } = useTranslation()
  const { guild, team } = useTeam()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { applicationId } = useParams()

  const { data: applicationDetails, isPending: isApplicationDetailsPending } =
    useApplicationDetails(
      team.id,
      applicationId ? parseInt(applicationId) : undefined,
    )

  const {
    data: { user },
  } = usePageInfo()
  const { data: userCharacters } = useUserCharacterDetails(!!user)
  const [submitting, setSubmitting] = useState(false)
  const [helpModalOpen, setHelpModalOpen] = useState(false)
  const [fileAttachments, setFileAttachments] = useState<
    Record<number, File[]>
  >({})
  const [existingAttachments, setExistingAttachments] = useState<
    Record<
      number,
      (wowaudit.ApplicationAnswerAttachment & { _destroy?: boolean })[]
    >
  >({})

  const editable =
    editing &&
    !!applicationId &&
    applicationDetails?.userId === user?.id &&
    ['submitted', 'under_review'].includes(applicationDetails?.status ?? '')

  const { data: pendingUserApplications, isPending } = useUserApplications(true)
  const { data: processedUserApplications, isPending: isPreviousAppsPending } =
    useUserApplications(false)

  const userApplicationsForTeam = useMemo(() => {
    if (!user) return []
    return [
      ...(pendingUserApplications || []),
      ...(processedUserApplications || []),
    ].filter((application) => application.teamId === team.id)
  }, [user, pendingUserApplications, processedUserApplications, team.id])

  const pendingApplicationForTeam = useMemo(
    () =>
      userApplicationsForTeam.find((a) =>
        ['submitted', 'under_review'].includes(a.status),
      ),
    [userApplicationsForTeam],
  )

  const hasDiscordLinked = useMemo(
    () =>
      user?.linkedAccounts?.some(
        (account) => account.displayType === 'Discord',
      ),
    [user],
  )

  const hasGoogleLinked = useMemo(
    () =>
      user?.linkedAccounts?.some((account) => account.displayType === 'Google'),
    [user],
  )

  const existingMemberCharacter = useMemo(() => {
    if (!user || !team.members) return undefined
    const memberCharacterIds = team.members.map((m) => m.characterReference.id)
    return user.characterReferences?.find((char) =>
      memberCharacterIds.includes(char.id),
    )
  }, [user, team.members])

  const mainCharacter = useMemo(
    () =>
      user?.characterReferences?.find((c) => c.main) ||
      user?.characterReferences?.[0],
    [user],
  )

  const formSchema = useMemo(
    () =>
      createFormSchema(
        team.recruitmentDetails?.show_country ?? false,
        team.recruitmentDetails?.show_age ?? false,
        team.questions?.filter((q) => q.required).map((q) => q.id) ?? [],
      ),
    [team.recruitmentDetails, team.questions],
  )

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      role: 'damage',
      character_id: mainCharacter?.id,
      country: '',
      age: null,
      notify_on_discord: hasDiscordLinked ?? false,
      notify_by_email: !hasDiscordLinked,
      answers: {},
    },
  })

  const { clearSavedDraft } = useDraftStorage({
    storageKey: `wowaudit_application_draft_${team.id}`,
    form,
    fieldsToSave: editable
      ? []
      : ['role', 'character_id', 'country', 'age', 'answers'],
  })

  useEffect(() => {
    if (editable && applicationDetails) {
      const answersMap: Record<string, string> = {}
      const existingAttachmentsMap: Record<
        number,
        wowaudit.ApplicationAnswerAttachment[]
      > = {}

      applicationDetails.answers.forEach((answer) => {
        const question = team.questions.find(
          (q) => q.description === answer.questionDescription,
        )
        if (question) {
          answersMap[question.id.toString()] = answer.response || ''
          if (answer.attachments.length > 0) {
            existingAttachmentsMap[question.id] = answer.attachments
          }
        }
      })

      form.reset({
        role: applicationDetails.role,
        character_id: applicationDetails.characterReference?.id,
        country: applicationDetails.country || '',
        age: applicationDetails.age || null,
        notify_on_discord: applicationDetails.notifyOnDiscord,
        notify_by_email: applicationDetails.notifyByEmail,
        answers: answersMap,
      })

      setExistingAttachments(existingAttachmentsMap)
    }
  }, [editable, applicationDetails, team.questions, form, hasDiscordLinked])

  const selectedCharacterId = form.watch('character_id')
  const notifyByEmail = form.watch('notify_by_email')
  const notifyOnDiscord = form.watch('notify_on_discord')

  const selectedCharacter = useMemo(
    () => user?.characterReferences?.find((c) => c.id === selectedCharacterId),
    [user, selectedCharacterId],
  )

  const selectedCharacterDetails = useMemo(
    () => userCharacters?.find((c) => c.id === selectedCharacterId),
    [userCharacters, selectedCharacterId],
  )

  const {
    refreshing: refreshingCharacterDetails,
    refreshData: refreshCharacterDetails,
  } = useRefreshData({
    retriever: updateCharacter,
    id: selectedCharacter?.id,
    params: {
      team_id: team.id.toString(),
      wcl_role: form.watch('role') ?? 'damage',
    },
    callback: () => {
      queryClient.invalidateQueries({ queryKey: ['userCharacterDetails'] })
    },
  })

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true)

    const formData = new FormData()
    formData.append('team_id', team.id.toString())
    formData.append('character_id', values.character_id.toString())
    formData.append('role', values.role)
    formData.append('notify_by_email', values.notify_by_email.toString())
    formData.append('notify_on_discord', values.notify_on_discord.toString())
    if (values.age) formData.append('age', values.age.toString())
    if (values.country) formData.append('country', values.country)

    const allQuestionIds = new Set(team.questions.map((q) => q.id))
    Array.from(allQuestionIds).forEach((questionId, index) => {
      formData.append(`answers[${index}][question_id]`, questionId.toString())
      formData.append(
        `answers[${index}][response]`,
        values.answers[questionId] || '',
      )

      if (editable) {
        const existingAnswer = applicationDetails?.answers.find(
          (a) =>
            a.questionDescription ===
            team.questions.find((q) => q.id === questionId)?.description,
        )
        if (existingAnswer) {
          formData.append(`answers[${index}][id]`, existingAnswer.id.toString())
        }

        const questionExistingAttachments =
          existingAttachments[questionId] || []
        questionExistingAttachments.forEach((attachment, attachmentIndex) => {
          formData.append(
            `answers[${index}][attachments_attributes][${attachmentIndex}][id]`,
            attachment.id.toString(),
          )
          if (attachment._destroy) {
            formData.append(
              `answers[${index}][attachments_attributes][${attachmentIndex}][_destroy]`,
              'true',
            )
          }
        })
      }

      ;(fileAttachments[questionId] || []).forEach((file) => {
        formData.append(`answers[${index}][files][]`, file)
      })
    })

    try {
      if (editable && applicationId) {
        await updateApplicationWithFormData(parseInt(applicationId), formData)
        queryClient.invalidateQueries({ queryKey: ['applicationDetails'] })
        navigate(`${team.path}${inDashboard ? '/apply' : ''}`)
      } else {
        await createApplication(formData)
        clearSavedDraft()
      }
      queryClient.invalidateQueries({ queryKey: ['team'] })
      queryClient.invalidateQueries({ queryKey: ['userApplications'] })
      toast.success(
        `Application ${editable ? 'updated' : 'submitted'} successfully`,
      )
    } catch (err) {
      displayErrorToast(err as Error, t)
    } finally {
      setSubmitting(false)
    }
  }

  const previousApplication = useMemo(
    () =>
      [...userApplicationsForTeam].sort(
        (a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf(),
      )[0],
    [userApplicationsForTeam],
  )

  const supplementalAlertToShow = () => {
    const shouldShowPreviousResult =
      previousApplication &&
      !editable &&
      (!existingMemberCharacter ||
        dayjs(previousApplication.updatedAt).isAfter(
          dayjs().subtract(1, 'month'),
        ))

    if (shouldShowPreviousResult) {
      return (
        <Alert
          className='my-6 text-foreground'
          variant={
            previousApplication.status === 'accepted'
              ? 'success'
              : previousApplication.status === 'withdrawn'
              ? 'info'
              : 'destructive'
          }
        >
          {previousApplication.status === 'accepted' ? (
            <CheckCircle2 className='h-4 w-4' />
          ) : (
            <XCircle className='h-4 w-4' />
          )}
          <AlertTitle>Previous application</AlertTitle>
          <AlertDescription>
            <div>
              Your application to this team was{' '}
              <strong>
                {applicationStatusLabels[
                  previousApplication.status
                ].toLowerCase()}
              </strong>{' '}
              {dayjs(previousApplication.updatedAt).fromNow()}.
            </div>

            <div className='flex items-center gap-2 mt-4'>
              <Link
                to={urlToMainSite(
                  `/profile/applications/${previousApplication.id}`,
                )}
                className={cn(
                  buttonVariants({ variant: 'outline', size: 'sm' }),
                )}
              >
                View
              </Link>
            </div>
          </AlertDescription>
        </Alert>
      )
    }

    if (existingMemberCharacter) {
      return (
        <Alert className='my-6 text-foreground'>
          <Info className='h-4 w-4' />
          <AlertTitle>You are already in this team</AlertTitle>
          <AlertDescription>
            Your character <strong>{existingMemberCharacter.name}</strong> is
            already a member of team {team.name}. Are you sure you want to
            apply?
          </AlertDescription>
        </Alert>
      )
    }
  }

  const details: Partial<TeamRecruitmentDetails> =
    team?.recruitmentDetails ?? {}

  const customStyles = inDashboard
    ? null
    : {
        css_background:
          details.css_background ?? defaultLegacyFormColors.css_background,
        css_body: details.css_body ?? defaultLegacyFormColors.css_body,
        css_headers: details.css_headers ?? defaultLegacyFormColors.css_headers,
        css_text: details.css_text ?? defaultLegacyFormColors.css_text,
        css_inputs: details.css_inputs ?? defaultLegacyFormColors.css_inputs,
        css_header_bar:
          details.css_header_bar ?? defaultLegacyFormColors.css_header_bar,
      }

  const content = (
    <>
      {!user ? (
        <>
          <Alert className='mb-6 text-foreground'>
            <Info className='h-4 w-4' />
            <AlertTitle>You are not logged in</AlertTitle>
            <AlertDescription>
              Please log in to submit your application.
            </AlertDescription>
          </Alert>
          <div className='flex justify-center mt-4'>
            <LoginButtons excludeGoogle={true} />
          </div>
        </>
      ) : isPending ||
        isPreviousAppsPending ||
        (isApplicationDetailsPending && editing && applicationId) ? (
        <Skeleton
          className={`h-screen w-full border-none ${
            customStyles ? 'bg-transparent' : ''
          }`}
        />
      ) : (
        <div>
          {editable ? (
            <Alert className='mb-6 mt-2'>
              <AlertTitle>Editing application</AlertTitle>
              <AlertDescription>
                You are editing your application to {guild.name} (team{' '}
                {team.name}).
              </AlertDescription>
              <Link
                to={`${team.path}${inDashboard ? '/apply' : ''}`}
                className={cn(
                  buttonVariants({ variant: 'outline', size: 'sm' }),
                  'mt-2',
                )}
              >
                Cancel
              </Link>
            </Alert>
          ) : (
            <>
              {team.recruitmentDetails?.introduction && (
                <div className='mt-2 mb-6 whitespace-pre-wrap'>
                  {team.recruitmentDetails?.introduction}
                </div>
              )}

              {supplementalAlertToShow()}
            </>
          )}

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className='space-y-6 mt-2'
            >
              <div className='flex gap-2 items-stretch'>
                <FormField
                  control={form.control}
                  name='role'
                  render={({ field }) => (
                    <FormItem>
                      <RoleSelector
                        className={cn(
                          'rounded-md',
                          customStyles ? '' : 'border bg-card-nested',
                        )}
                        style={
                          customStyles
                            ? { backgroundColor: customStyles.css_inputs }
                            : {}
                        }
                        value={field.value}
                        onChange={field.onChange}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className='flex flex-col gap-2 grow'>
                  <Label>Contact info</Label>
                  <div
                    className={`flex flex-col gap-2 grow justify-center rounded-md border bg-card-nested p-3 ${
                      customStyles ? 'border-none' : 'border bg-card-nested'
                    }`}
                    style={
                      customStyles
                        ? { backgroundColor: customStyles.css_inputs }
                        : {}
                    }
                  >
                    <div className='flex gap-4'>
                      <div className='flex items-center gap-2 w-28'>
                        <img
                          src={imageUrl('bnet-logo-blue.svg')}
                          className='w-5 shrink-0'
                        />{' '}
                        <span
                          className={cn(
                            'truncate ',
                            customStyles ? '' : 'text-muted-foreground',
                          )}
                        >
                          Battletag
                        </span>
                      </div>
                      <span className='truncate'>{user?.battletag || '-'}</span>
                    </div>

                    <div className='flex gap-4'>
                      <div className='flex items-center gap-2 w-28'>
                        <img
                          src={imageUrl('discord-logo-blue.svg')}
                          className='w-5 shrink-0'
                        />{' '}
                        <span
                          className={cn(
                            'truncate ',
                            customStyles ? '' : 'text-muted-foreground',
                          )}
                        >
                          Discord ID
                        </span>
                      </div>
                      {hasDiscordLinked ? (
                        <span className='truncate'>
                          {user?.linkedAccounts?.find(
                            (account) => account.displayType === 'Discord',
                          )?.displayName || '-'}
                        </span>
                      ) : (
                        <CompactLinkedAccount type='Discord' buttonOnly />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className='flex flex-col gap-2 grow'>
                <Label>Main character</Label>
                {selectedCharacter ? (
                  <ApplicationCharacterCard
                    team={team}
                    guild={guild}
                    className={cn(
                      'rounded-md p-3',
                      customStyles ? 'border-none' : 'border bg-card-nested',
                    )}
                    style={
                      customStyles
                        ? { backgroundColor: customStyles.css_inputs }
                        : {}
                    }
                    onEditChange={(value) => {
                      form.setValue('character_id', parseInt(value))

                      const newSelectedCharacter = userCharacters?.find(
                        (c) => c.id === parseInt(value),
                      )

                      if (
                        dayjs(newSelectedCharacter?.refreshedAt).isBefore(
                          dayjs().subtract(1, 'day'),
                        )
                      ) {
                        refreshCharacterDetails(parseInt(value))
                      }
                    }}
                    loading={refreshingCharacterDetails}
                    character={selectedCharacter}
                    characterDetails={
                      selectedCharacterDetails?.details as
                        | wowaudit.CharacterDetails
                        | undefined
                    }
                  />
                ) : (
                  <Alert className='mb-6 text-foreground' variant='warning'>
                    <Info className='h-4 w-4' />
                    <AlertTitle>No characters found</AlertTitle>
                    <AlertDescription>
                      <div className='flex justify-between items-center gap-2'>
                        <span>{t('errors.no_user_characters_found')}</span>

                        <Button
                          type='button'
                          variant='outline'
                          size='xs'
                          onClick={() => setHelpModalOpen(true)}
                        >
                          Help
                        </Button>
                      </div>

                      {helpModalOpen && (
                        <TroubleshootingModal
                          inProfile={false}
                          setOpen={(value: boolean) => setHelpModalOpen(value)}
                        />
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              <div className='flex *:grow gap-4'>
                {team.recruitmentDetails?.show_country ? (
                  <FormField
                    control={form.control}
                    name='country'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Country
                          <span className='text-destructive-foreground ml-1'>
                            *
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder='Your country'
                            className={cn(
                              'rounded-md',
                              customStyles
                                ? 'border-none'
                                : 'border bg-card-nested',
                            )}
                            style={
                              customStyles
                                ? { backgroundColor: customStyles.css_inputs }
                                : {}
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : null}

                {team.recruitmentDetails?.show_age ? (
                  <FormField
                    control={form.control}
                    name='age'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Age
                          <span className='text-destructive-foreground ml-1'>
                            *
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            type='number'
                            placeholder='Your age'
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value
                                  ? parseInt(e.target.value)
                                  : null,
                              )
                            }
                            className={cn(
                              'rounded-md',
                              customStyles
                                ? 'border-none'
                                : 'border bg-card-nested',
                            )}
                            style={
                              customStyles
                                ? { backgroundColor: customStyles.css_inputs }
                                : {}
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : null}
              </div>

              {team.questions && team.questions.length > 0 && (
                <div className='space-y-4'>
                  {team.questions.map((question) => (
                    <FormField
                      key={question.id}
                      control={form.control}
                      name={`answers.${question.id}`}
                      render={() => (
                        <FormItem>
                          {question.questionType === 'checkbox' ? null : (
                            <FormLabel>
                              {question.description}
                              {question.required && (
                                <span className='text-destructive-foreground ml-1'>
                                  *
                                </span>
                              )}
                            </FormLabel>
                          )}

                          <FormControl>
                            <ApplicationQuestion
                              question={question}
                              fileAttachments={
                                fileAttachments[question.id] || []
                              }
                              onFileAttachmentsChange={(attachments) =>
                                setFileAttachments((prev) => ({
                                  ...prev,
                                  [question.id]: attachments,
                                }))
                              }
                              existingAttachments={
                                existingAttachments[question.id] || []
                              }
                              onExistingAttachmentDelete={(attachmentId) =>
                                setExistingAttachments((prev) => ({
                                  ...prev,
                                  [question.id]: prev[question.id]?.map((a) =>
                                    a.id === attachmentId
                                      ? { ...a, _destroy: !a._destroy }
                                      : a,
                                  ),
                                }))
                              }
                              className={cn(
                                'rounded-md',
                                customStyles
                                  ? 'border-none'
                                  : 'border bg-card-nested',
                              )}
                              style={
                                customStyles
                                  ? { backgroundColor: customStyles.css_inputs }
                                  : {}
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              )}

              <div
                className={cn(
                  'space-y-4 pt-4 border-t',
                  customStyles ? '' : 'border-accent',
                )}
              >
                <Label>
                  How would you like to be notified of updates to your
                  application?
                </Label>

                <div className='flex gap-8 mt-2'>
                  <FormField
                    control={form.control}
                    name='notify_on_discord'
                    render={({ field }) => (
                      <FormItem className='flex items-center gap-2'>
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className='cursor-pointer'>
                          Discord
                        </FormLabel>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='notify_by_email'
                    render={({ field }) => (
                      <FormItem className='flex items-center gap-2'>
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className='cursor-pointer'>Email</FormLabel>
                      </FormItem>
                    )}
                  />
                </div>

                {notifyOnDiscord && !hasDiscordLinked && (
                  <CompactLinkedAccount
                    type='Discord'
                    message='Connect your Discord account in your profile to receive notifications.'
                  />
                )}

                {notifyByEmail && !hasGoogleLinked && (
                  <CompactLinkedAccount
                    type='Google'
                    message='Connect your Google account in your profile to receive email notifications.'
                  />
                )}
              </div>

              <div className='flex justify-end gap-3'>
                {team.recruitmentDetails?.discord_link ? (
                  <JoinDiscordButton
                    className={customStyles ? 'text-accent-foreground' : ''}
                    to={team.recruitmentDetails.discord_link}
                  />
                ) : null}
                <Button
                  type='submit'
                  loading={submitting}
                  className={customStyles ? 'text-accent-foreground' : ''}
                  style={
                    customStyles
                      ? {
                          background: customStyles.css_header_bar,
                        }
                      : {}
                  }
                >
                  {editable ? 'Update' : 'Submit'}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      )}
    </>
  )

  if (
    pendingApplicationForTeam &&
    !editable &&
    (!editing || !isApplicationDetailsPending || !applicationId)
  ) {
    return (
      <UserApplicationDetails
        application={pendingApplicationForTeam}
        teamReference={team}
        inLegacyForm={!inDashboard}
        recruitmentDetails={team.recruitmentDetails}
      />
    )
  }

  if (inDashboard) {
    return (
      <div className='max-w-3xl w-full'>
        <h2 className='mb-4'>Apply to {guild.name}</h2>
        <Card className='pt-4 px-4 pb-6'>{content}</Card>
      </div>
    )
  }

  return content
}
